import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { PublicLayout } from '@/layouts/PublicLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { CustomerLayout } from '@/layouts/CustomerLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { RouteGuard } from '@/components/portal/RouteGuard';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

// Public
const Home = lazy(() => import('@/pages/Home'));
const About = lazy(() => import('@/pages/About'));
const Plans = lazy(() => import('@/pages/Plans'));
const Gallery = lazy(() => import('@/pages/Gallery'));
const Contact = lazy(() => import('@/pages/Contact'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// Auth
const Login = lazy(() => import('@/pages/auth/Login'));
const Register = lazy(() => import('@/pages/auth/Register'));
const ForgotPassword = lazy(async () => ({
  default: (await import('@/pages/auth/PasswordFlow')).ForgotPasswordPage,
}));
const ResetPassword = lazy(async () => ({
  default: (await import('@/pages/auth/PasswordFlow')).ResetPasswordPage,
}));

// Customer
const CustomerDashboard = lazy(() => import('@/pages/customer/Dashboard'));

// Admin
// (page modules loaded lazily below)

const s = (n: ReactNode) => <Suspense fallback={<LoadingSpinner fullscreen />}>{n}</Suspense>;

// Customer sub-page loader
const cp = (name: keyof typeof import('@/pages/customer/CustomerPages')) =>
  lazy(async () => {
    const mod = await import('@/pages/customer/CustomerPages');
    return { default: mod[name] as React.ComponentType };
  });
const ap = (name: keyof typeof import('@/pages/admin/AdminPages')) =>
  lazy(async () => {
    const mod = await import('@/pages/admin/AdminPages');
    return { default: mod[name] as React.ComponentType };
  });

const CustomerProfile = lazy(async () => ({ default: (await import('@/pages/customer/Dashboard')).ProfilePage }));
const BankPage = cp('BankPage');
const WalletPage = cp('WalletPage');
const TransactionsPage = cp('TransactionsPage');
const ReferralsPage = cp('ReferralsPage');
const TreePage = cp('TreePage');
const NotifsPage = cp('NotificationsPage');
const CustomerSettingsPage = cp('CustomerSettingsPage');
const SupportPage = cp('SupportPage');
const WithdrawalsPage = cp('WithdrawalsPage');

const AdminDashboard = ap('AdminDashboard');
const AdminUsers = ap('AdminUsers');
const AdminUserDetail = ap('AdminUserDetail');
const AdminPlans = ap('AdminPlans');
const AdminMedia = ap('AdminMedia');
const AdminLeaders = ap('AdminLeaders');
const AdminChiefGuests = ap('AdminChiefGuests');
const AdminLicenses = ap('AdminLicenses');
const AdminContact = ap('AdminContact');
const AdminNotifications = ap('AdminNotifications');
const AdminSettings = ap('AdminSettings');
const AdminManualPayments = ap('AdminManualPayments');
const AdminAuditLogs = ap('AdminAuditLogs');
const AdminReports = ap('AdminReports');
const AdminCashbackQueue = ap('AdminCashbackQueue');
const AdminCommissionsQueue = ap('AdminCommissionsQueue');
const AdminWithdrawalQueue = ap('AdminWithdrawalQueue');
const AdminTreeExplorer = ap('AdminTreeExplorer');

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: s(<Home />) },
      { path: '/about', element: s(<About />) },
      { path: '/plans', element: s(<Plans />) },
      { path: '/gallery', element: s(<Gallery />) },
      { path: '/contact', element: s(<Contact />) },
    ],
  },
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { path: 'login', element: s(<Login />) },
      { path: 'admin-login', element: s(<Login />) },
      { path: 'register', element: s(<Register />) },
      { path: 'forgot-password', element: s(<ForgotPassword />) },
      { path: 'reset-password', element: s(<ResetPassword />) },
    ],
  },
  {
    path: '/dashboard',
    element: (
      <RouteGuard required="customer">
        <CustomerLayout />
      </RouteGuard>
    ),
    children: [
      { index: true, element: s(<CustomerDashboard />) },
      { path: 'profile', element: s(<CustomerProfile />) },
      { path: 'bank', element: s(<BankPage />) },
      { path: 'wallet', element: s(<WalletPage />) },
      { path: 'withdrawals', element: s(<WithdrawalsPage />) },
      { path: 'transactions', element: s(<TransactionsPage />) },
      { path: 'referrals', element: s(<ReferralsPage />) },
      { path: 'tree', element: s(<TreePage />) },
      { path: 'notifications', element: s(<NotifsPage />) },
      { path: 'settings', element: s(<CustomerSettingsPage />) },
      { path: 'support', element: s(<SupportPage />) },
    ],
  },
  {
    path: '/admin',
    element: (
      <RouteGuard required="admin">
        <AdminLayout />
      </RouteGuard>
    ),
    children: [
      { index: true, element: s(<AdminDashboard />) },
      { path: 'users', element: s(<AdminUsers />) },
      { path: 'users/:id', element: s(<AdminUserDetail />) },
      { path: 'plans', element: s(<AdminPlans />) },
      { path: 'media', element: s(<AdminMedia />) },
      { path: 'leaders', element: s(<AdminLeaders />) },
      { path: 'chief-guests', element: s(<AdminChiefGuests />) },
      { path: 'licenses', element: s(<AdminLicenses />) },
      { path: 'contact', element: s(<AdminContact />) },
      { path: 'notifications', element: s(<AdminNotifications />) },
      { path: 'settings', element: s(<AdminSettings />) },
      { path: 'payments', element: s(<AdminManualPayments />) },
      { path: 'cashback', element: s(<AdminCashbackQueue />) },
      { path: 'commissions', element: s(<AdminCommissionsQueue />) },
      { path: 'withdrawals', element: s(<AdminWithdrawalQueue />) },
      { path: 'tree', element: s(<AdminTreeExplorer />) },
      { path: 'audit-logs', element: s(<AdminAuditLogs />) },
      { path: 'reports', element: s(<AdminReports />) },
    ],
  },
  { path: '*', element: s(<NotFound />) },
]);
